import React, { useCallback, useEffect, useState } from 'react';
import { PortalApiError, portalApi } from '../api';
import { getAccessToken } from '../authStorage';
import { PASSWORD_MIN_LENGTH, SMS_CODE_MAX_LENGTH, SMS_COOLDOWN_SECONDS } from '../constants';
import { maskPhone } from '../functions';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
/**
 * 「账号与安全」：绑定手机号、短信改密、7 天冷静期自助销户。
 */
export default function AccountSecurityPanel({ onBack, onLogout, user }) {
    const [confirmPassword, setConfirmPassword] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [smsCooldown, setSmsCooldown] = useState(0);
    const [success, setSuccess] = useState('');
    const [view, setView] = useState('list');
    const masked = maskPhone(user.phone) || M.PROFILE_SECURITY_UNBOUND;
    useEffect(() => {
        if (smsCooldown <= 0) {
            return;
        }
        const timer = window.setInterval(() => {
            setSmsCooldown(prev => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => window.clearInterval(timer);
    }, [smsCooldown > 0]);
    const requireToken = () => {
        const token = getAccessToken();
        if (!token) {
            setError(M.NEED_LOGIN_FIRST);
            logger.warn(`[账号与安全] ${new Date().toISOString()} 缺少 accessToken`);
        }
        return token;
    };
    const goList = () => {
        setView('list');
        setError('');
        setSuccess('');
        setCode('');
        setNewPassword('');
        setConfirmPassword('');
    };
    const onHeaderBack = () => {
        if (view === 'list') {
            onBack();
            return;
        }
        goList();
    };
    const onSendSms = useCallback(async () => {
        setError('');
        const token = requireToken();
        if (!token) {
            return;
        }
        try {
            await portalApi.sendChangePasswordSms(token);
            setSmsCooldown(SMS_COOLDOWN_SECONDS);
            logger.info(`[账号与安全] ${new Date().toISOString()} 已发送改密验证码 | phone=${maskPhone(user.phone)}`);
        }
        catch (e) {
            const message = e instanceof PortalApiError ? e.message : (e?.message || M.SEND_SMS_FAILED);
            setError(message);
            logger.warn(`[账号与安全] ${new Date().toISOString()} 发送改密验证码失败 | ${message}`);
        }
    }, [user.phone]);
    const onSubmitPassword = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');
        if (newPassword.length < PASSWORD_MIN_LENGTH) {
            setError(M.PROFILE_PASSWORD_TOO_SHORT);
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(M.PROFILE_PASSWORD_MISMATCH);
            return;
        }
        const token = requireToken();
        if (!token) {
            return;
        }
        setLoading(true);
        try {
            await portalApi.changePassword(token, {
                code,
                newPassword
            });
            setSuccess(M.PROFILE_PASSWORD_CHANGED);
            setCode('');
            setNewPassword('');
            setConfirmPassword('');
            logger.info(`[账号与安全] ${new Date().toISOString()} 修改密码成功 | userId=${user.id}`);
            setView('list');
        }
        catch (e) {
            const message = e instanceof PortalApiError ? e.message : (e?.message || M.ACTION_FAILED);
            setError(message);
            logger.warn(`[账号与安全] ${new Date().toISOString()} 修改密码失败 | ${message}`);
        }
        finally {
            setLoading(false);
        }
    };
    const onConfirmDelete = async () => {
        setError('');
        const token = requireToken();
        if (!token) {
            return;
        }
        setLoading(true);
        try {
            const result = await portalApi.requestAccountDeletion(token);
            logger.info(`[账号与安全] ${new Date().toISOString()} 已申请注销 | userId=${user.id} purgeAt=${result.purgeAt}`);
            onLogout();
        }
        catch (e) {
            const message = e instanceof PortalApiError ? e.message : (e?.message || M.ACTION_FAILED);
            setError(message);
            logger.warn(`[账号与安全] ${new Date().toISOString()} 申请注销失败 | ${message}`);
            setLoading(false);
        }
    };
    const title = view === 'password'
        ? M.PROFILE_SECURITY_PASSWORD
        : view === 'delete'
            ? M.PROFILE_SECURITY_DELETE
            : M.PROFILE_MENU_SECURITY;
    return (React.createElement("div", { className: 'portal-profile portal-profile-detail-view' },
        React.createElement("header", { className: 'portal-profile-detail-header' },
            React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-profile-detail-back', onClick: onHeaderBack, type: 'button' }, '‹'),
            React.createElement("span", { className: 'portal-profile-detail-title' }, title)),
        React.createElement("div", { className: 'portal-profile-detail-body' },
            view === 'list' && (React.createElement(React.Fragment, null,
                success ? React.createElement("div", { className: 'portal-security-success' }, success) : null,
                error ? React.createElement("div", { className: 'portal-error' }, error) : null,
                React.createElement("section", { className: 'portal-security-list', "aria-label": M.PROFILE_MENU_SECURITY },
                    React.createElement("div", { className: 'portal-security-row' },
                        React.createElement("span", { className: 'portal-security-label' }, M.PROFILE_SECURITY_BIND),
                        React.createElement("span", { className: 'portal-security-value' }, masked)),
                    React.createElement("button", { className: 'portal-security-row portal-security-row-btn', onClick: () => {
                            setError('');
                            setSuccess('');
                            setView('password');
                            logger.info(`[账号与安全] ${new Date().toISOString()} 打开修改密码`);
                        }, type: 'button' },
                        React.createElement("span", { className: 'portal-security-label' }, M.PROFILE_SECURITY_PASSWORD),
                        React.createElement("span", { className: 'portal-security-meta' },
                            M.PROFILE_SECURITY_PASSWORD_HINT,
                            React.createElement("span", { "aria-hidden": true, className: 'portal-profile-menu-chevron' }, '›'))),
                    React.createElement("button", { className: 'portal-security-row portal-security-row-btn', onClick: () => {
                            setError('');
                            setSuccess('');
                            setView('delete');
                            logger.info(`[账号与安全] ${new Date().toISOString()} 打开注销账号`);
                        }, type: 'button' },
                        React.createElement("span", { className: 'portal-security-label' }, M.PROFILE_SECURITY_DELETE),
                        React.createElement("span", { className: 'portal-security-meta' },
                            M.PROFILE_SECURITY_DELETE_HINT,
                            React.createElement("span", { "aria-hidden": true, className: 'portal-profile-menu-chevron' }, '›')))))),
            view === 'password' && (React.createElement("form", { className: 'portal-security-form', onSubmit: onSubmitPassword },
                React.createElement("p", { className: 'portal-security-hint' },
                    M.PROFILE_PASSWORD_SMS_HINT,
                    " ",
                    masked),
                React.createElement("label", { className: 'portal-field' },
                    M.SMS_CODE,
                    React.createElement("div", { className: 'portal-sms-row' },
                        React.createElement("input", { maxLength: SMS_CODE_MAX_LENGTH, onChange: e => setCode(e.target.value), placeholder: M.SMS_CODE_PLACEHOLDER, value: code }),
                        React.createElement("button", { disabled: smsCooldown > 0 || loading, onClick: onSendSms, type: 'button' }, smsCooldown > 0
                            ? `${smsCooldown}${M.SMS_COOLDOWN_SUFFIX}`
                            : M.SEND_SMS)),
                    React.createElement("div", { className: 'portal-sms-tip' }, M.SMS_NOT_CONNECTED_TIP)),
                React.createElement("label", { className: 'portal-field' },
                    M.PROFILE_NEW_PASSWORD,
                    React.createElement("input", { autoComplete: 'new-password', onChange: e => setNewPassword(e.target.value), placeholder: M.PASSWORD_PLACEHOLDER, type: 'password', value: newPassword })),
                React.createElement("label", { className: 'portal-field' },
                    M.PROFILE_CONFIRM_PASSWORD,
                    React.createElement("input", { autoComplete: 'new-password', onChange: e => setConfirmPassword(e.target.value), placeholder: M.PASSWORD_PLACEHOLDER, type: 'password', value: confirmPassword })),
                error ? React.createElement("div", { className: 'portal-error' }, error) : null,
                React.createElement("button", { className: 'portal-primary-btn', disabled: loading, type: 'submit' }, loading ? M.SUBMITTING : M.PROFILE_SECURITY_PASSWORD))),
            view === 'delete' && (React.createElement("div", { className: 'portal-security-form' },
                React.createElement("p", { className: 'portal-security-hint' }, M.PROFILE_DELETE_WARNING),
                error ? React.createElement("div", { className: 'portal-error' }, error) : null,
                React.createElement("button", { className: 'portal-security-danger-btn', disabled: loading, onClick: onConfirmDelete, type: 'button' }, loading ? M.SUBMITTING : M.PROFILE_DELETE_CONFIRM))))));
}
